import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { combineLatest, EMPTY, from, Observable, of, OperatorFunction } from 'rxjs';
import { bufferTime, catchError, debounceTime, filter, map, mergeMap, skip, switchMap, tap } from 'rxjs/operators';
import {
  DEFAULT_PAGINATED_STATE_CONFIG,
  type PaginatedStateConfig,
  PaginatedStateLoadingMode,
  PaginatedStateModel,
} from './paginated-store.types';

/**
 * Abstract base class for stores that implement pagination, search, and deferred count loading.
 *
 * This class provides common functionality for paginated entity stores including:
 * - Cursor-based pagination with previous/next token tracking
 * - Debounced search with automatic re-counting
 * - Deferred loading of item-specific counts/metadata
 * - Loading state management (loading, counting, searching, deleting)
 * - Error handling
 * - Page size changes
 *
 * @template TData The data type for items (must have id property)
 * @template TState The state interface extending PaginatedStoreState<TData>
 *
 * ## Basic Usage Example
 *
 * For simple stores without deferred loading:
 *
 * @example
 * ```typescript
 * interface MyItemData {
 *   id: string;
 *   name: string;
 *   customField: string;
 * }
 *
 * interface MyItemStoreState extends PaginatedStoreState<MyItemData> {}
 *
 * @Injectable()
 * export class MyItemStore extends PaginatedStore<MyItemData, MyItemStoreState> {
 *   constructor(
 *     @Inject(MY_REPOSITORY) private readonly repository: MyRepository,
 *     @Inject(CONFIG) config: PaginatedStoreConfig
 *   ) {
 *     super(undefined, config);
 *     // That's it! Base class handles all default initialization
 *     // Even the config is optional
 *   }
 *
 *   // Implement required abstract methods
 *   protected async countRepository(searchTerm?: string): Promise<number> {
 *     return this.repository.count(searchTerm);
 *   }
 *
 *   protected listFlow(
 *     limit: number,
 *     lastItem: MyItemData | undefined,
 *     searchTerm: string | undefined
 *   ): Observable<MyItemData[]> {
 *     // Use whatever property makes sense for your pagination cursor
 *     return from(this.repository.getAll(searchTerm, limit, lastItem?.name));
 *   }
 *
 *   protected processDeferredAttributes(_itemId: string): Observable<Partial<MyItemData>> {
 *     // No counts/metadata are needed to be loaded
 *     return of();
 *   }
 *
 *   protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
 *     // Implement the deletion of the item from the repository
 *     // Or return false, if deletion is not supported
 *   }
 * }
 * ```
 *
 * ## Accumulated Loading Mode (Infinite Scroll)
 *
 * For infinite scroll UIs where items accumulate instead of replacing:
 *
 * @example
 * ```typescript
 * @Injectable()
 * export class MyItemStore extends PaginatedStore<MyItemData, MyItemStoreState> {
 *   constructor(@Inject(MY_REPOSITORY) private readonly repository: MyRepository) {
 *     super(undefined, {
 *       loadingMode: 'accumulated', // Enable infinite scroll mode
 *     });
 *   }
 *
 *   // Same implementation as paginated mode - no changes needed!
 *   protected async countRepository(searchTerm?: string): Promise<number> {
 *     return this.repository.count(searchTerm);
 *   }
 *
 *   protected listFlow(
 *     limit: number,
 *     lastItem: MyItemData | undefined,
 *     searchTerm: string | undefined
 *   ): Observable<MyItemData[]> {
 *     return from(this.repository.getAll(searchTerm, limit, lastItem?.name));
 *   }
 *
 *   protected processDeferredAttributes(_itemId: string): Observable<Partial<MyItemData>> {
 *     // No counts/metadata are needed to be loaded
 *     return of();
 *   }
 *
 *   protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
 *     // Implement the deletion of the item from the repository
 *     // Or return false, if deletion is not supported
 *   }
 * }
 * ```
 *
 * **Behavior differences in accumulated mode:**
 * - New items are appended to the list instead of replacing current page
 * - Search term changes restart the list from the beginning
 * - Page size changes restart the list from the beginning
 * - Backward pagination is not supported (forward-only scrolling)
 *
 * ## Advanced Usage: Deferred Count Loading
 *
 * For stores that need to load additional metadata/counts asynchronously after the main list loads:
 *
 * @example
 * ```typescript
 * interface MyItemData {
 *   id: string;
 *   name: string;
 *   itemCountA?: number; // undefined = not loaded yet
 *   itemCountB?: number; // undefined = not loaded yet
 * }
 *
 * @Injectable()
 * export class MyItemStore extends PaginatedStore<MyItemData, MyItemStoreState> {
 *   constructor(
 *     @Inject(MY_REPOSITORY) private readonly repository: MyRepository,
 *     @Inject(RELATED_REPOSITORY) private readonly relatedRepository: RelatedRepository
 *   ) {
 *     super(undefined, {
 *       maxConcurrentCountQueries: 2, // Limit concurrent queries for deferred loading
 *     });
 *   }
 *
 *   // Handle deferred loading, emitting all the updates to be applied to the item
 *   protected processDeferredAttributes(itemId: string): Observable<Partial<MyItemData>> {
 *     const itemCountsA$ = this.countAttribute(this.relatedRepository.countForItem(itemId), itemId).pipe(
 *       map((itemCounts) => ({ itemCountA: itemCounts }))
 *     );
 *     const itemCountsB$ = this.countAttribute(this.relatedRepository.countForItem(itemId), itemId).pipe(
 *       map((itemCounts) => ({ itemCountB: itemCounts }))
 *     );
 *     return merge(itemCountsA$, itemCountsB$);
 *   }
 *
 *   // and the other methods...
 * }
 * ```
 *
 * ## Advanced Usage with Additional State
 *
 * For stores that need extra state properties beyond the base pagination state:
 *
 * @example
 * ```typescript
 * interface MyStoreState extends PaginatedStoreState<MyItemData> {
 *   environmentFilter?: string; // Custom state property
 *   saving: boolean; // Another custom property
 * }
 *
 * @Injectable()
 * export class MyItemStore extends PaginatedStore<MyItemData, MyStoreState> {
 *   constructor(@Inject(MY_REPOSITORY) private readonly repository: MyRepository) {
 *     super({
 *       environmentFilter: undefined,
 *       saving: false,
 *     });
 *   }
 *
 *   // Additional selectors for custom state
 *   public readonly environmentFilter$ = this.select(state => state.environmentFilter);
 *   public readonly saving$ = this.select(state => state.saving);
 *
 *   // and the other methods...
 * }
 * ```
 *
 * ## Public API for Components
 *
 * Subclasses automatically get these public observables:
 * - `items$` - Current page of items (paginated mode) or accumulated list (accumulated mode)
 * - `loading$` - Main loading state
 * - `counting$` - Counting total items
 * - `searching$` - Search in progress
 * - `deleting$` - Delete operation in progress
 * - `error$` - Error message (if any)
 * - `nextToken$` - Next page token, contains the last item of the current page
 * - `totalCount$` - Total number of items (with search term filters)
 * - `hasMoreItems$` - Whether more pages exist
 * - `noItems$` - True when no items found after loading
 * - `pageSize$` - Current page size
 * - `previousTokens$` - Previous page tokens, contains the last item of the previous page
 * - `itemsLoaded$` - True when items are loaded
 * - `noItems$` - True when no items found after loading

 * Updaters:
 * - `setSearchTerm(term: string)` - Update search term (triggers debounced search, clears list)
 * - `changePageSize(pageSize: number)` - Change page size (clears list and restarts)
 *
 * Effects:
 * - `initializeItems()` - Load first page
 * - `loadMoreItems(lastItem)` - Load next page (appends in accumulated mode)
 * - `relistPage()` - Refresh current page (or all loaded items in continuous mode)
 * - `countItems()` - Count items (triggers counting and updates total count)
 * - `deleteItem(itemId)` - Delete item (triggers deletion and updates the list)
 *
 * Helper methods:
 * - `countAttribute(countOperation: Promise<number>, itemId: string, defaultValue: number = 0): Observable<number>` - Count an attribute of an item.
 * - `handleItemAfterDatasetRemoval(itemId: string): void` - Handle item removal after dataset removal.
 * - `itemsUpdateTransform(items: EntityState<TData>, state: PaginatedStoreState<TData>): EntityState<TData>` - Transform items before updates are confirmed.
 */
/** @deprecated This class is deprecated and should not be used for new code. Use PaginatedState instead. */
export abstract class PaginatedStore<
  TData extends { id: string },
  TState extends PaginatedStateModel<TData>
> extends ComponentStore<TState> {
  protected readonly config: Required<PaginatedStateConfig>;
  protected readonly itemsAdapter: EntityAdapter<TData>;

  constructor(
    additionalState?: Partial<Omit<TState, keyof PaginatedStateModel<TData>>>,
    config?: Partial<PaginatedStateConfig>
  ) {
    // Create entity adapter first
    const adapter = createEntityAdapter<TData>({
      selectId: (item) => item.id,
    });

    // Initialize with default base state + any additional state from child stores
    super({
      items: adapter.getInitialState(),
      deferredAttributeMemory: {},
      loading: true,
      counting: true,
      searching: false,
      deleting: false,
      error: undefined,
      nextToken: undefined,
      hasMoreItems: true,
      searchTerm: undefined,
      totalCount: 0,
      pageSize: config?.initialPageSize ?? 10,
      previousPageTokens: [],
      ...additionalState,
    } as unknown as TState);

    this.config = { ...DEFAULT_PAGINATED_STATE_CONFIG, ...config };
    this.itemsAdapter = adapter;

    // Initialize search effect, skipping initialization values
    this.searchItems(this.searchTerm$.pipe(skip(1)));
    this.onPageSizeChange(this.pageSize$.pipe(skip(1)));
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly items$ = this.select((state) => this.itemsAdapter.getSelectors().selectAll(state.items));
  public readonly loading$ = this.select((state) => state.loading);
  public readonly counting$ = this.select((state) => state.counting);
  public readonly searching$ = this.select((state) => state.searching);
  public readonly deleting$ = this.select((state) => state.deleting);
  public readonly error$ = this.select((state) => state.error);
  public readonly nextToken$ = this.select((state) => state.nextToken);
  public readonly hasMoreItems$ = this.select((state) => state.hasMoreItems);
  public readonly searchTerm$ = this.select((state) => state.searchTerm);
  public readonly totalCount$ = this.select((state) => state.totalCount);
  public readonly pageSize$ = this.select((state) => state.pageSize);
  public readonly previousTokens$ = this.select((state) => state.previousPageTokens);
  public readonly itemsLoaded$ = this.select((state) => state.items.ids.length > 0);

  public readonly noItems$ = combineLatest([this.items$, this.totalCount$, this.counting$, this.searching$]).pipe(
    map(([items, totalCount, counting, searching]) => {
      return items.length === 0 && totalCount === 0 && !counting && !searching;
    })
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  protected readonly setError = this.updater((state, error: string | undefined) => ({
    ...state,
    error,
    loading: false,
    deleting: false,
  }));

  protected readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

  protected readonly setCounting = this.updater((state, counting: boolean) => ({ ...state, counting }));

  protected readonly setSearching = this.updater((state, searching: boolean) => ({ ...state, searching }));

  protected readonly setDeleting = this.updater((state, deleting: boolean) => ({ ...state, deleting }));

  protected readonly setTotalCount = this.updater((state, totalCount: number) => ({
    ...state,
    totalCount,
    counting: false,
  }));

  protected readonly updateItems = this.updater((state, { items, lastItem }: { items: TData[]; lastItem?: TData }) => {
    const updatedPreviousTokens = this.updatePreviousTokens(state.previousPageTokens, lastItem);

    // Choose adapter method based on loading mode
    const updatedItems =
      this.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS && lastItem !== undefined
        ? this.itemsAdapter.addMany(items, state.items) // Append in accumulated mode
        : this.itemsAdapter.setAll(items, state.items); // Replace in paginated mode

    // Apply any transform operations to the items before the updates are confirmed
    const transformedItems = this.itemsUpdateTransform(updatedItems, state);

    return {
      ...state,
      items: transformedItems,
      hasMoreItems: items.length === state.pageSize,
      previousPageTokens: this.config.loadingMode === 'paginated' ? updatedPreviousTokens : [],
      nextToken: items.length > 0 ? items[items.length - 1] : undefined,
      error: undefined,
      loading: false,
      searching: false,
      deleting: false,
    };
  });

  public readonly setSearchTerm = this.updater((state, searchTerm: string) => ({
    ...state,
    searchTerm: searchTerm.trim() === '' ? undefined : searchTerm,
    items: this.itemsAdapter.removeAll(state.items), // Clear items for fresh start
    hasMoreItems: true,
    previousPageTokens: [],
    nextToken: undefined,
    searching: true,
    loading: true,
  }));

  public readonly changePageSize = this.updater((state, pageSize: number) => ({
    ...state,
    pageSize,
    items: this.itemsAdapter.removeAll(state.items), // Clear items for fresh start
    previousPageTokens: [],
    nextToken: undefined,
    searching: false,
    loading: true,
  }));

  public readonly removeItem = this.updater((state, itemId: string) => ({
    ...state,
    items: this.itemsAdapter.removeOne(itemId, state.items),
    deleting: false,
  }));

  /**
   * Helper updater for updating individual items by id (useful for deferred count loading)
   */
  protected readonly updateItemById = this.updater(
    (state, { id, changes }: { id: string; changes: Partial<TData> }) => {
      return {
        ...state,
        items: this.itemsAdapter.updateOne({ id, changes }, state.items),
      };
    }
  );

  /**
   * Updater for storing deferred attributes in the memory.
   */
  protected readonly storeDeferredAttributes = this.updater(
    (state, { itemId, changes }: { itemId: string; changes: Partial<TData> }) => {
      const currentMemory = this.get((state) => state.deferredAttributeMemory);
      const currentChanges = currentMemory[itemId];
      return {
        ...state,
        deferredAttributeMemory: {
          ...currentMemory,
          [itemId]: { ...currentChanges, ...changes },
        },
      };
    }
  );

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Effect to count items with optional search term
   */
  public readonly countItems = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.setCounting(true)),
      concatLatestFrom(() => [this.searchTerm$]),
      switchMap(([, searchTerm]) =>
        from(this.countRepository(searchTerm)).pipe(
          tap((count) => this.setTotalCount(count)),
          this.carefulErrors()
        )
      )
    )
  );

  /**
   * Main list effect - loads items
   */
  protected readonly listItems = this.effect(
    (action$: Observable<{ searchTerm?: string; lastItem?: TData; limit?: number }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.pageSize$]),
        switchMap(([{ searchTerm, lastItem, limit: requestedLimit }, pageSize]) =>
          this.consolidateFlow(this.listFlow(requestedLimit ?? pageSize, lastItem, searchTerm), lastItem)
        ),
        this.carefulErrors()
      )
  );

  private readonly accumulateMoreItems = this.effect((action$: Observable<TData | undefined>) =>
    action$.pipe(
      concatLatestFrom(() => [this.hasMoreItems$]),
      filter(([, hasMoreItems]) => hasMoreItems),
      concatLatestFrom(() => [this.searchTerm$, this.nextToken$]),
      tap(([[lastItem], searchTerm, nextToken]) => {
        this.listItems({ searchTerm, lastItem: lastItem ?? nextToken });
      })
    )
  );

  /**
   * Load more effect (pagination).
   */
  public readonly loadMoreItems = this.effect((action$: Observable<TData | undefined>) =>
    action$.pipe(
      tap(() => this.setLoading(true)),
      concatLatestFrom(() => [this.searchTerm$]),
      tap(([lastItem, searchTerm]) => {
        if (this.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS) {
          this.accumulateMoreItems(lastItem);
        } else {
          this.listItems({ searchTerm, lastItem });
        }
      })
    )
  );

  /**
   * Search effect with debounce
   */
  private readonly searchItems = this.effect((searchTerm$: Observable<string | undefined>) =>
    searchTerm$.pipe(
      debounceTime(200),
      tap((searchTerm: string | undefined) => {
        this.countItems();
        this.listItems({ searchTerm, lastItem: undefined });
      })
    )
  );

  /**
   * Page size change effect
   */
  protected readonly onPageSizeChange = this.effect((action$: Observable<number>) =>
    action$.pipe(
      concatLatestFrom(() => [this.searchTerm$]),
      tap(([pageSize, searchTerm]) => this.listItems({ searchTerm, lastItem: undefined, limit: pageSize }))
    )
  );

  /**
   * Initialize and load items
   */
  public readonly initializeItems = this.effect((action$: Observable<void>) =>
    action$.pipe(
      tap(() => {
        this.countItems();
        this.listItems({ searchTerm: undefined, lastItem: undefined });
      })
    )
  );

  /**
   * Relist the current page, refreshing the items with fresh data from the repository.
   * For paginated mode: Fetches the same page again using the current page token.
   * For continuous mode: Fetches all items from the beginning up to the current loaded count.
   */
  public readonly relistPage = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.setLoading(true)),
      concatLatestFrom(() => [this.searchTerm$, this.previousTokens$, this.items$, this.pageSize$]),
      tap(([, searchTerm, previousTokens, items, pageSize]) => {
        if (this.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS) {
          // For continuous mode, relist all items from the beginning
          // Use the next multiple of pageSize that fits all current items, or pageSize if no items loaded
          const limit = items.length > 0 ? Math.ceil(items.length / pageSize) * pageSize : pageSize;
          this.listItems({ searchTerm, lastItem: undefined, limit });
        } else {
          // For paginated mode, relist the current page using the last token
          const lastToken = previousTokens.length > 0 ? previousTokens[previousTokens.length - 1] : undefined;
          this.listItems({ searchTerm, lastItem: lastToken, limit: pageSize });
        }
      }),
      this.carefulErrors()
    )
  );

  /**
   * Delete item effect.
   * Calls the repository deletion method and handles the item removal from the store,
   * if the operation was successful.
   */
  public readonly deleteItem = this.effect((action$: Observable<string>) =>
    action$.pipe(
      tap(() => this.setDeleting(true)),
      switchMap((itemId) =>
        from(this.deleteRepositoryItem(itemId)).pipe(
          tap((success) => {
            if (success) {
              this.handleItemAfterDatasetRemoval(itemId);
            } else {
              this.setDeleting(false);
            }
          }),
          catchError((error) => {
            this.patchState({
              deleting: false,
              error: error.message,
            } as Partial<TState>);
            return EMPTY;
          })
        )
      )
    )
  );

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Helper method to retrieve deferred attributes from the memory.
   * @param itemId The ID of the item to retrieve the deferred attributes for.
   * @returns The deferred attributes for the item, or undefined if no attributes are stored.
   */
  private retrieveDeferredAttributes(itemId: string): Partial<TData> | undefined {
    if (!this.config.useMemory) {
      return undefined;
    } else {
      const currentMemory = this.get((state) => state.deferredAttributeMemory);
      return currentMemory[itemId] ?? undefined;
    }
  }

  /**
   * Helper method to consolidate items into the store and trigger deferred count loading.
   * @param items The items to consolidate
   * @param lastItem The last item from the previous page
   */
  private consolidateItems(items: TData[], lastItem: TData | undefined): void {
    // Update the items in the store
    this.updateItems({ items, lastItem });

    // Trigger deferred count loading for each item, in parallel
    from(items)
      .pipe(
        mergeMap((item) => {
          const deferredAttributes = this.retrieveDeferredAttributes(item.id);
          if (deferredAttributes) {
            this.updateItemById({ id: item.id, changes: deferredAttributes });
            return of(undefined);
          } else {
            return this.processDeferredAttributes(item.id).pipe(
              bufferTime(200), // Avoid overwhelming the store with too many updates
              tap((changesBatch) => {
                if (changesBatch.length > 0) {
                  // Merge all partial changes in the batch into one object
                  const mergedChanges = Object.assign({}, ...changesBatch);
                  this.updateItemById({ id: item.id, changes: mergedChanges });
                  if (this.config.useMemory) {
                    this.storeDeferredAttributes({ itemId: item.id, changes: mergedChanges });
                  }
                }
              })
            );
          }
        }, this.config.maxConcurrentCountQueries)
      )
      .subscribe();
  }

  /**
   * Helper method to consolidate items into the store and trigger deferred count loading.
   * If the provided items are undefined, the consolidation effect will not be triggered.
   * @param items The items to consolidate
   * @param lastItem The last item from the previous page
   * @returns Observable of items array
   */
  private consolidateFlow(items: Observable<TData[] | undefined>, lastItem: TData | undefined): Observable<TData[]> {
    return items.pipe(
      filter((items) => items !== undefined),
      tap((items: TData[]) => this.consolidateItems(items, lastItem)),
      this.carefulErrors()
    );
  }

  protected carefulErrors<T>(): OperatorFunction<T, T> {
    return catchError((error) => {
      console.error(error);
      this.setError(error.message);
      return EMPTY;
    });
  }

  /**
   * Helper method to handle item removal. This method should be called after an item is removed from the database
   * that supports the data managed by the store.
   * @param itemId The ID of the item to remove
   */
  protected handleItemAfterDatasetRemoval(itemId: string): void {
    // Recount items
    this.countItems();

    // In paginated mode, reload current page to fill the gap
    // In continuous mode, just remove the item from the list
    if (this.config.loadingMode === PaginatedStateLoadingMode.PAGINATED) {
      const previousTokens = this.get((state) => state.previousPageTokens);
      const lastToken = previousTokens.length > 0 ? previousTokens[previousTokens.length - 1] : undefined;
      this.loadMoreItems(lastToken);
    } else {
      this.removeItem(itemId);
    }
  }

  /**
   * Updates the pagination token history
   */
  protected updatePreviousTokens(
    previousTokens: (TData | undefined)[],
    nextToken?: TData | undefined
  ): (TData | undefined)[] {
    const nextTokenIndex = previousTokens.findIndex((token) => token?.id === nextToken?.id);
    return nextTokenIndex > -1 ? previousTokens.slice(0, nextTokenIndex + 1) : [...previousTokens, nextToken];
  }

  /**
   * Helper method to count an attribute of an item.
   * @param countOperation The operation to count the attribute.
   * @param itemId The ID of the item to count the attribute for.
   * @param defaultValue The default value to return if the operation fails. Defaults to 0.
   * @returns Observable resolving to the count of the attribute.
   */
  protected countAttribute(
    countOperation: Promise<number>,
    itemId: string,
    defaultValue = 0
  ): Observable<number> {
    return from(countOperation).pipe(
      map((count) => count),
      catchError((error) => {
        console.error(`Error loading counts for ${itemId}:`, error);
        return of(defaultValue);
      })
    );
  }

  // ============================================================================
  // Abstract Methods (must be implemented by subclasses)
  // ============================================================================

  /**
   * Count items in the repository with optional search term
   * @param searchTerm Optional search term to filter items
   * @returns Promise resolving to the total count
   */
  protected abstract countRepository(searchTerm?: string): Promise<number>;

  /**
   * Abstract method to delete an item from the repository. This method should be implemented by the subclass to handle
   * the deletion of an item from the repository that supports the data managed by the store.
   * @param itemId The ID of the item to remove
   * * @returns Promise that lets you know if the operation was successful
   */
  protected abstract deleteRepositoryItem(itemId: string): Promise<boolean>;

  /**
   * List items from the repository/use-case with pagination
   * @param limit Maximum number of items to return
   * @param lastItem Last item from previous page (for cursor pagination)
   * @param searchTerm Optional search term to filter items
   * @returns Observable of items array
   */
  protected abstract listFlow(
    limit: number,
    lastItem: TData | undefined,
    searchTerm: string | undefined
  ): Observable<TData[]>;

  /**
   * Load counts/metadata for a specific item (deferred loading)
   * This method should be implemented by the subclass to handle the loading of counts/metadata for a specific item.
   * Leave empty if no counts/metadata are needed to be loaded for a specific store.
   * @param itemId The ID of the item to load counts for
   * @returns Observable resolving to the changes to be applied to the item. Could be only one change or multiple changes over time.
   */
  protected abstract processDeferredAttributes(itemId: string): Observable<Partial<TData>>;

  // ============================================================================
  // Protected Methods (can be overridden by subclasses if needed)
  // ============================================================================

  /**
   * Helper method to transform the items before they are updated.
   * This method can be overridden by subclasses if items need a specific transformation before updates are confirmed.
   * For example, if items need to be sorted or filtered before updates are confirmed.
   * It can also be used for performing other operations that depend on the items being updated.
   * @param items The items to transform
   * @param state The state of the store
   * @returns The transformed items
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected itemsUpdateTransform(items: EntityState<TData>, state: PaginatedStateModel<TData>): EntityState<TData> {
    return items;
  }
}
